import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch,
  FiUpload,
  FiDownload,
  FiTrash2,
  FiEdit3,
  FiX,
  FiCheck,
  FiImage,
  FiGrid,
  FiEye,
  FiArrowRight,
  FiMail,
  FiArrowUpRight,
} from "react-icons/fi";
import { FaGithub, FaLinkedin, FaInstagram, FaWhatsapp } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css"; // design system: tokens, buttons, grain, .kk-progress
import "./Gallery.css"; // everything unique to this page

/* ═══════════════ DATA (presentational only — logic untouched) ═══════════════ */

const GALLERY_TABS = ["All", "Travel", "College", "Projects", "Nature"];

// Local posters used when a remote image fails to load.
const FALLBACK_IMG = [
  "/img/proj-notes.jpg",
  "/img/about-workspace.jpg",
  "/img/proj-listing.jpg",
  "/img/cert-hackathon.jpg",
  "/img/proj-darkhackers.jpg",
  "/img/proj-products.jpg",
  "/img/proj-realtycore.jpg",
  "/img/karan.jpg",
];

// Demo items for preview when Firebase is not configured.
// (In your project with real `db`, these are never used — onSnapshot loads Firestore.)
const DEMO_IMAGES = [
  { id: "demo-1", title: "Travel - Mountain Sunrise", base64: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-2", title: "Projects - Code Setup", base64: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-3", title: "Nature - Forest Path", base64: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-4", title: "College - Campus Days", base64: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-5", title: "Travel - Night Coding Trip", base64: "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-6", title: "Projects - Dev Desk", base64: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-7", title: "Nature - Foggy Hills", base64: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?q=80&w=600&auto=format&fit=crop" },
  { id: "demo-8", title: "College - Hackathon Night", base64: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=600&auto=format&fit=crop" },
];

const SOCIALS = [
  { icon: <FaGithub />, href: "https://github.com/Karanks1436", label: "GitHub" },
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/karanks1436", label: "Instagram" },
  { icon: <FiMail />, href: "mailto:karanksxxx@gmail.com", label: "Email" },
  { icon: <FaWhatsapp />, href: "https://wa.me/919041429065", label: "WhatsApp" },
  { icon: <SiLeetcode />, href: "https://leetcode.com/u/karanks1436/", label: "LeetCode" },
];

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

// ─── Floating Particles ────────────────────────────────────────
function GalleryParticles() {
  const parts = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="gl-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="gl-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── Skeleton Loader ───────────────────────────────────────────
function SkeletonCard({ index }) {
  return (
    <motion.div
      className="gl-skel"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
    >
      <div className="gl-skel-img" style={{ aspectRatio: index % 3 === 0 ? "3/4" : "4/3" }} />
      <div className="gl-skel-line" />
    </motion.div>
  );
}

// ─── Gallery Card ──────────────────────────────────────────────
function GalleryCard({ image, index, isAdmin, onView, onEdit, onDelete, onDownload }) {
  const [isHovered, setIsHovered] = useState(false);
  const localFallback = FALLBACK_IMG[index % FALLBACK_IMG.length];

  return (
    <motion.div
      className="gl-card"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay: (index % 4) * 0.06, duration: 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="gl-img-wrap" onClick={() => onView(image.base64)}>
        <img
          src={image.base64}
          alt={image.title}
          className="gl-img"
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = localFallback;
            }
          }}
        />

        <span className="gl-tag">{image.title.split(" - ")[0]}</span>

        <motion.div
          className="gl-hover"
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.span
            className="gl-overlay-ic"
            initial={{ scale: 0 }}
            animate={{ scale: isHovered ? 1 : 0 }}
            transition={{ delay: 0.08 }}
          >
            <FiEye />
          </motion.span>
          <span className="gl-overlay-hint">Open</span>
        </motion.div>
      </div>

      <div className="gl-foot">
        <span className="gl-cap" title={image.title}>
          {image.title}
        </span>

        <div className="gl-actions">
          <motion.button
            className="gl-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(image.base64, image.title);
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title="Download"
            type="button"
          >
            <FiDownload />
          </motion.button>

          {isAdmin && (
            <>
              <motion.button
                className="gl-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(image.id, image.title);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Edit title"
                type="button"
              >
                <FiEdit3 />
              </motion.button>
              <motion.button
                className="gl-icon-btn is-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(image.id);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Delete"
                type="button"
              >
                <FiTrash2 />
              </motion.button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────
function EditModal({ title, onTitleChange, onSave, onCancel }) {
  return (
    <motion.div
      className="gl-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="gl-modal"
        initial={{ scale: 0.94, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 20, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gl-modal__head">
          <div>
            <span className="kk-eyebrow">Admin — Gallery</span>
            <h2>Edit title</h2>
          </div>
          <motion.button
            className="gl-modal__x"
            onClick={onCancel}
            whileHover={{ rotate: 90 }}
            aria-label="Close"
            type="button"
          >
            <FiX />
          </motion.button>
        </div>

        <div className="gl-modal__body">
          <label className="gl-field">
            <span>Image title</span>
            <input type="text" value={title} onChange={onTitleChange} autoFocus />
          </label>

          <div className="gl-modal__btns">
            <button className="kk-btn kk-btn--ghost" onClick={onCancel} type="button">
              Cancel
            </button>
            <motion.button
              className="kk-btn"
              onClick={onSave}
              whileTap={{ scale: 0.95 }}
              type="button"
            >
              <FiCheck /> Save
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Lightbox ──────────────────────────────────────────────────
function Lightbox({ imageUrl, onClose, onDownload, title }) {
  if (!imageUrl) return null;

  return (
    <motion.div
      className="gl-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="gl-lightbox-box"
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.88, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <motion.button
          className="gl-lightbox-x"
          onClick={onClose}
          whileHover={{ rotate: 90, scale: 1.1 }}
          aria-label="Close"
          type="button"
        >
          <FiX />
        </motion.button>

        <img src={imageUrl} alt="Preview" className="gl-lightbox-img" />

        {onDownload && (
          <motion.button
            className="kk-btn gl-lightbox-dl"
            onClick={() => onDownload(imageUrl, title || "image")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.95 }}
            type="button"
          >
            <FiDownload /> Download
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Empty State ───────────────────────────────────────────────
function EmptyState({ searchTerm }) {
  return (
    <motion.div className="gl-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="gl-empty__ic">
        <FiImage />
      </div>
      <h3>{searchTerm ? "No images found" : "Gallery is empty"}</h3>
      <p>
        {searchTerm ? (
          <>
            No results for &ldquo;<strong>{searchTerm}</strong>&rdquo; — try another keyword.
          </>
        ) : (
          "Images will appear here once uploaded."
        )}
      </p>
    </motion.div>
  );
}

// ─── Main Gallery Component ────────────────────────────────────
// LOGIC: identical to your original — same state, same handlers, same
// Firebase calls. Only styling/classes, parallax and preview fallback added.
export default function Gallery() {
  const rootRef = useRef(null);
  const [activeTab, setActiveTab] = useState("All");
  const [visibleCount, setVisibleCount] = useState(8);
  const [selectedImage, setSelectedImage] = useState(null);
  const [images, setImages] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useParallax(rootRef);

  const isAdmin = user?.email === "karanksxxx@gmail.com";

  useEffect(() => {
    if (!auth || !db) {
      try {
        const remembered = JSON.parse(localStorage.getItem("user"));
        if (remembered) setUser({ email: remembered.Email, ...remembered });
      } catch {
        /* ignore */
      }
      const t = setTimeout(() => {
        setImages(DEMO_IMAGES);
        setLoading(false);
      }, 600);
      return () => clearTimeout(t);
    }
    const unsubAuth = onAuthStateChanged(auth, setUser);

    const q = query(collection(db, "Gallery"), orderBy("timestamp", "desc"));
    const unsubGallery = onSnapshot(q, (snap) => {
      setImages(
        snap.docs.map((d) => ({
          id: d.id,
          base64: d.data().base64,
          title: d.data().title || "Untitled",
        }))
      );
      setLoading(false);
    });

    return () => {
      unsubAuth();
      unsubGallery();
    };
  }, []);

  // Upload handler
  const handleUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !isAdmin) return;

    if (!db) {
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          setImages((p) => [
            {
              id: "local-" + Date.now() + Math.random(),
              base64: reader.result,
              title: file.name.replace(/\.[^/.]+$/, ""),
            },
            ...p,
          ]);
        };
        reader.readAsDataURL(file);
      });
      e.target.value = "";
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await addDoc(collection(db, "Gallery"), {
            base64: reader.result,
            title: file.name.replace(/\.[^/.]+$/, ""),
            timestamp: serverTimestamp(),
          });
        } catch (err) {
          console.error("Upload error:", err);
        }
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  // Edit handlers
  const startEdit = (id, currentTitle) => {
    setEditId(id);
    setEditTitle(currentTitle);
  };

  const saveEdit = async () => {
    if (!editTitle.trim() || !editId) return;
    if (!db || String(editId).startsWith("demo-") || String(editId).startsWith("local-")) {
      setImages((p) => p.map((im) => (im.id === editId ? { ...im, title: editTitle } : im)));
      setEditId(null);
      setEditTitle("");
      return;
    }
    try {
      await updateDoc(doc(db, "Gallery", editId), { title: editTitle });
      setEditId(null);
      setEditTitle("");
    } catch (err) {
      console.error("Edit error:", err);
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditTitle("");
  };

  // Delete handler
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
      setImages((p) => p.filter((im) => im.id !== id));
      return;
    }
    try {
      await deleteDoc(doc(db, "Gallery", id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // Download handler
  const downloadImage = (base64, title) => {
    const a = document.createElement("a");
    a.href = base64;
    a.download = `${title.replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filteredImages = images.filter((img) => {
    const matchSearch = img.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === "All") return matchSearch;
    return matchSearch && img.title.toLowerCase().includes(activeTab.toLowerCase());
  });
  const visibleImages = filteredImages.slice(0, visibleCount);

  return (
    <div className="kk-root gl-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <GalleryParticles />


      {/* ══════════════ HERO ══════════════ */}
      <header className="gl-hero">
        <div className="gl-hero__bg" aria-hidden="true">
          <span className="gl-hero__grid" data-plx="0.05" />
          <span className="gl-hero__glow gl-hero__glow--acid" data-plx="0.16" />
          <span className="gl-hero__glow gl-hero__glow--violet" data-plx="0.24" />
          <span className="gl-hero__word" data-plx="0.1">
            FRAMES
          </span>
        </div>

        <div className="kk-wrap gl-hero__in">
          <motion.div
            className="gl-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="gl-kicker">
              <span className="kk-dot" />
              <span>Gallery</span>
              <span className="kk-slash">/</span>
              <span>Media</span>
            </div>

            <h1 className="gl-title">
              <motion.span
                className="gl-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                My <span className="kk-serif kk-acid">Gallery</span>
              </motion.span>
              <motion.span
                className="gl-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                moments, framed.
              </motion.span>
            </h1>

            <p className="gl-lead">
              A collection of moments, designs and creative works captured in pixels —
              travel, campus, builds and the hills in between.
            </p>

            <div className="gl-hero__meta">
              <div>
                <b>{images.length || 8}</b>
                <span>Frames</span>
              </div>
              <div>
                <b>05</b>
                <span>Albums</span>
              </div>
              <div>
                <b>∞</b>
                <span>Moments</span>
              </div>
            </div>
          </motion.div>

          <motion.aside
            className="gl-hero__panel"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.16 }}
          >
            <div className="gl-hero__panel-top">
              <span className="gl-hero__panel-dot" />
              <span>Frame Index</span>
              <span className="gl-hero__panel-code">KKS·1436</span>
            </div>

            <div className="gl-hero__panel-num">
              <b>{String(images.length || 8).padStart(2, "0")}</b>
              <span>
                frames
                <br />
                online
              </span>
            </div>

            <dl className="gl-hero__panel-rows">
              <div>
                <dt>Albums</dt>
                <dd>Travel · Campus · Builds</dd>
              </div>
              <div>
                <dt>Format</dt>
                <dd>Full-res PNG</dd>
              </div>
              <div>
                <dt>Access</dt>
                <dd>Free · login to save</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>Firestore live</dd>
              </div>
            </dl>
          </motion.aside>
        </div>
      </header>

      {/* ══════════════ TOOLBAR ══════════════ */}
      <div className="gl-bar">
        <div className="kk-wrap gl-bar__in">
          <div className="gl-tabs">
            {GALLERY_TABS.map((t) => (
              <button
                key={t}
                className={`gl-tab ${activeTab === t ? "is-active" : ""}`}
                onClick={() => {
                  setActiveTab(t);
                  setVisibleCount(8);
                }}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>

          <div className="gl-search">
            <FiSearch className="gl-search__ic" />
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <motion.button
                className="gl-search__x"
                onClick={() => setSearchTerm("")}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                aria-label="Clear search"
                type="button"
              >
                <FiX />
              </motion.button>
            )}
          </div>

          <span className="gl-count">
            <FiGrid /> {filteredImages.length} images
          </span>

          {isAdmin && (
            <motion.button
              className="kk-btn gl-upload-btn"
              onClick={() => fileInputRef.current.click()}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              type="button"
            >
              <FiUpload /> Upload
            </motion.button>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleUpload}
          />
        </div>
      </div>

      {/* ══════════════ MAIN ══════════════ */}
      <main className="kk-section kk-tight gl-main">
        <div className="kk-wrap">
          <div className="gl-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">02 — Collection</span>
              <h2 className="kk-h2">
                All
                <br />
                <span className="kk-serif">images</span>
              </h2>
            </div>
            <div className="gl-sec-head__aside">
              <p>
                Every frame is a real moment — shot on the phone, screenshotted at 2 a.m.,
                or rendered for a project. Tap any tile to open it full screen.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="gl-masonry">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} index={i} />
              ))}
            </div>
          ) : filteredImages.length === 0 ? (
            <EmptyState searchTerm={searchTerm} />
          ) : (
            <>
              <div className="gl-masonry">
                <AnimatePresence>
                  {visibleImages.map((image, index) => (
                    <GalleryCard
                      key={image.id}
                      image={image}
                      index={index}
                      isAdmin={isAdmin}
                      onView={setSelectedImage}
                      onEdit={startEdit}
                      onDelete={handleDelete}
                      onDownload={downloadImage}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {visibleCount < filteredImages.length && (
                <div className="gl-more">
                  <button
                    className="kk-btn kk-btn--ghost kk-btn--lg"
                    onClick={() => setVisibleCount((c) => c + 8)}
                    type="button"
                  >
                    View more <FiArrowRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* ══════════════ CTA ══════════════ */}
      <section className="gl-cta">
        <div className="gl-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap gl-cta__in">
          <span className="kk-eyebrow">Behind the lens</span>
          <h2 className="kk-h2">
            Want a frame from{" "}
            <span className="kk-serif kk-acid">a project?</span>
          </h2>
          <p>Say the word and I will pull the original, full resolution, no watermark.</p>

          <div className="gl-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Gallery%20request">
              <FiMail /> Request an image
            </a>
            <a className="kk-btn kk-btn--ghost kk-btn--lg" href="https://instagram.com/karanks1436" target="_blank" rel="noreferrer">
              <FiArrowUpRight /> Follow the feed
            </a>
          </div>

          <div className="gl-cta__socials">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("mailto:") ? undefined : "_blank"}
                rel="noreferrer"
                aria-label={s.label}
                title={s.label}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </section>
   <Navbar />
      <Foot />

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      {/* ═══════════ MODALS ═══════════ */}
      <AnimatePresence>
        {editId && (
          <EditModal
            title={editTitle}
            onTitleChange={(e) => setEditTitle(e.target.value)}
            onSave={saveEdit}
            onCancel={cancelEdit}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedImage && (
          <Lightbox
            imageUrl={selectedImage}
            onClose={() => setSelectedImage(null)}
            onDownload={downloadImage}
            title="Gallery Image"
          />
        )}
      </AnimatePresence>
    </div>
  );
}